'use client';

import { useState, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import Link from 'next/link';
import {
  LayoutDashboard,
  FileText,
  Store,
  Users,
  ShoppingCart,
  Wallet,
  Settings,
  ChevronRight,
  ChevronDown,
  Receipt,
} from 'lucide-react';

// Dashboard sidebar navigation items
const sidebarNavItems = [
  {
    title: 'Dashboard',
    href: '/dashboard',
    icon: <LayoutDashboard className="w-5 h-5" />,
  },
  {
    title: 'My Lead Lists',
    href: '/dashboard/lead-lists',
    icon: <FileText className="w-5 h-5" />,
  },
  {
    title: 'My Scraper Listings',
    href: '/dashboard/scrapers',
    icon: <Store className="w-5 h-5" />,
  },
  {
    title: 'My Group Buys',
    href: '/dashboard/group-buys',
    icon: <Users className="w-5 h-5" />,
  },
  {
    title: 'My Purchases',
    href: '/dashboard/purchases',
    icon: <ShoppingCart className="w-5 h-5" />,
    subItems: [
      { title: 'Purchased Lists', href: '/dashboard/purchases/lists' },
      { title: 'Ordered Scrapers', href: '/dashboard/purchases/scrapers' },
      { title: 'Joined Group Buys', href: '/dashboard/purchases/group-buys' },
    ],
  },
  {
    title: 'Sales',
    href: '/dashboard/sales',
    icon: <Store className="w-5 h-5" />,
  },
  {
    title: 'Orders',
    href: '/dashboard/orders',
    icon: <Receipt className="w-5 h-5" />,
  },
];

export default function DashboardLayout({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({
    'My Purchases': false,
  });

  // Toggle sidebar item expansion
  const toggleExpand = (title: string) => {
    setExpandedItems((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  // Check if the current path matches the nav item without highlighting
  const isActive = (href: string) => {
    if (pathname === href) return true;
    if (href !== '/dashboard' && pathname?.startsWith(href + '/')) return true;
    if (href === '/dashboard') return pathname === '/dashboard';
    return false;
  };

  return (
    <div className="flex flex-col min-h-screen">
      {/* ADDED WRAPPER for row layout */}
      <div className="flex flex-1">
        {/* Desktop Sidebar */}
        <aside className="hidden md:block w-64 border-r bg-card h-[calc(100vh-4rem)] sticky z-10">
          <div className="flex flex-col py-6 px-3 space-y-6 overflow-y-auto h-full">
            <div className="px-3">
              <h2 className="text-lg font-semibold">Dashboard</h2>
              <p className="text-sm text-muted-foreground">Manage your account</p>
            </div>
            <nav className="space-y-1">
              {sidebarNavItems.map((item) => (
                <div key={item.title}>
                  {item.subItems ? (
                    <div className="space-y-1">
                      <button
                        onClick={() => toggleExpand(item.title)}
                        className={`w-full flex items-center justify-between px-3 py-2 text-sm rounded-md transition-colors ${
                          isActive(item.href)
                            ? 'bg-primary text-primary-foreground'
                            : 'hover:bg-accent hover:text-accent-foreground'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          {item.icon}
                          <span>{item.title}</span>
                        </div>
                        {expandedItems[item.title] ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </button>
                      {expandedItems[item.title] && (
                        <div className="pl-9 space-y-1 mt-1">
                          {item.subItems.map((subItem) => (
                            <Link
                              key={subItem.href}
                              href={subItem.href}
                              className={`block px-3 py-2 text-sm rounded-md transition-colors ${
                                isActive(subItem.href)
                                  ? 'bg-primary/90 text-primary-foreground'
                                  : 'hover:bg-accent hover:text-accent-foreground'
                              }`}
                            >
                              {subItem.title}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link
                      href={item.href}
                      className={`flex items-center gap-3 px-3 py-2 text-sm rounded-md transition-colors ${
                        isActive(item.href)
                          ? 'bg-primary text-primary-foreground'
                          : 'hover:bg-accent hover:text-accent-foreground'
                      }`}
                    >
                      {item.icon}
                      <span>{item.title}</span>
                    </Link>
                  )}
                </div>
              ))}
            </nav>
          </div>
        </aside>

        {/* Main Content */}
        <div className="flex flex-col flex-1 overflow-y-auto">
          <main className="flex-1 p-4 md:p-6">{children}</main>
        </div>
      </div>{' '}
      {/* END ADDED WRAPPER */}
    </div>
  );
}
